import { useState } from "react";
import { Box, Typography, Button, Divider, Link, Menu, MenuItem, ListItemText, ListSubheader } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import StarIcon from '@mui/icons-material/Star';
import { useDispatch, useSelector } from "react-redux";
import { fotoDe } from "../../common/imparte";
import { startChat } from "../../../actions/chat";
import { useIrAEntrar } from "../../../utils/destinoAlEntrar";
import { rolProveedor } from "../../../utils/rolProveedor";

const ProfileCard = ({ user, profile }) => {
  const dispatch = useDispatch();
  const authUser = useSelector(state => state.user.user);
  const isAuthenticated = useSelector(state => state.user.loading === false && state.user.user._id);
  // Sin sesión, «Contactar» manda a entrar; al entrar regresa a este perfil
  const irAEntrar = useIrAEntrar();
  const [anclaTemas, setAnclaTemas] = useState(null);

  // «Contactar»: el servidor solo deja empezar una conversación sobre un servicio o una página de la persona. Con uno solo se abre
  // el chat sobre eso; con varios se pregunta sobre cuál. No sale en tu propio perfil ni si no hay sobre qué escribirle.
  const temas = [
    ...(profile?.servicios || []).map(s => ({ id: s._id, tipo: 'service', nombre: s.nombre, etiqueta: 'Servicio' })),
    ...(profile?.paginas || []).map(p => ({ id: p._id, tipo: 'page', nombre: p.nombre, etiqueta: 'Página' })),
  ];
  const esMiPerfil = Boolean(authUser?._id) && String(authUser._id) === String(user?._id);
  const puedeContactar = temas.length > 0 && !esMiPerfil;
  const desc = String(profile?.perfil?.desc || '').trim();

  const escribirSobre = (tema) => {
    setAnclaTemas(null);
    dispatch(startChat(user._id, tema.id, tema.tipo));
  };
  const contactar = (e) => {
    if (!isAuthenticated) {
      irAEntrar();
      return;
    }
    if (temas.length === 1) {
      escribirSobre(temas[0]);
      return;
    }
    setAnclaTemas(e.currentTarget);
  };

  // Calificación del proveedor = promedio ponderado de sus servicios (por nº de reseñas)
  const serviciosCalificados = (profile?.servicios || []).filter(s => s.ratingPromedio > 0 && s.ratingTotal > 0);
  const totalResenas = serviciosCalificados.reduce((acc, s) => acc + s.ratingTotal, 0);
  const ratingProveedor = totalResenas > 0
    ? serviciosCalificados.reduce((acc, s) => acc + s.ratingPromedio * s.ratingTotal, 0) / totalResenas
    : 0;

  // Con la versión de la foto en la dirección: al cambiarla se ve la nueva (antes `?v=${user.version}`, que no existe, y siempre `<id>.jpg`).
  // A lo más 900 px de ancho: se ve en un recuadro de 350 px de alto, no hace falta la original completa
  const avatarUrl = fotoDe(user, "w_900,c_limit");

  return (
    <Box className="profile-card-container">
      <Box className="profile-image-section">
        <img
          src={avatarUrl || "/default-avatar.png"}
          alt={user?.name}
          className="profile-big-avatar"
        />
        <Box className="profile-name-overlay">
          <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>
            {user?.name} {user?.apellido}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
              {/* Su rol o, si no escribió ninguno, las categorías de sus servicios (antes «Experto» de todos) */}
              {rolProveedor(profile?.perfil, profile?.servicios)}
            </Typography>
            {ratingProveedor > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StarIcon sx={{ color: 'primary.main', fontSize: 18, mr: 0.2 }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{ratingProveedor.toFixed(1)}</Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Nuevo</Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Divider sx={{ mb: 2 }} />
        {desc ? (
          <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6, mb: 3 }}>
            {desc}
          </Typography>
        ) : esMiPerfil && (
          // Sin descripción, quien visita no ve nada (antes «Hola, no se ha proporcionado una descripción todavía.», como si lo hubiera
          // escrito la persona); la persona, en su propio perfil, ve dónde escribirla
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 3 }}>
            Aún no tienes una descripción. <Link component={RouterLink} to="/mi-perfil">Escríbela en tu perfil</Link>, en «Sobre ti»,
            para que tus clientes sepan a qué te dedicas.
          </Typography>
        )}

        {puedeContactar && (
          <>
            <Button
              variant="contained"
              fullWidth
              onClick={contactar}
              aria-haspopup={temas.length > 1 ? 'menu' : undefined}
              aria-controls={anclaTemas ? 'menu-contactar' : undefined}
              aria-expanded={anclaTemas ? 'true' : undefined}
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                py: 1
              }}
            >
              Contactar
            </Button>
            <Menu
              id="menu-contactar"
              anchorEl={anclaTemas}
              open={Boolean(anclaTemas)}
              onClose={() => setAnclaTemas(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <ListSubheader sx={{ lineHeight: 1.5, py: 1, color: 'primary.main', fontWeight: 700 }}>
                ¿Sobre qué quieres escribirle?
              </ListSubheader>
              {temas.map(tema => (
                <MenuItem key={`${tema.tipo}-${tema.id}`} onClick={() => escribirSobre(tema)}>
                  <ListItemText primary={tema.nombre} secondary={tema.etiqueta} />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ProfileCard;
